import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import type { LocationCardProps } from '@elfsight/widget-location-card/common';
import { LocationCard as LocationCardComponent } from '@elfsight/widget-location-card/widget';
import { useSettings } from '@wu/widget-core';
import type { DetailCardElement } from '../../types';
import { useActiveLocation } from '../active-location-context';
import { isDisplayable } from '../components/displayable';

export function LocationCard({
  verticalPadding,
  horizontalPadding,
  footerComponent
}: {
  verticalPadding: number;
  horizontalPadding: number;
  footerComponent: ReactNode;
}) {
  const {
    detailedCardHeadingStyle,
    detailedCardTextStyle,
    dayMonthFormat,
    firstWeekday,
    timeFormat,
    openStatusColor,
    closedSoonStatusColor,
    closedStatusColor,
    textColor,
    accentColor,
    language,
    detailCardBusinessHoursDisplay,
    detailCardElements,
    ctaButtonsStyle,
    ctaButtonsSize,
    elementsCornerRadius,
    editMode,
    categories
  } = useSettings();
  const { activeLocation, prevActiveLocationRef } = useActiveLocation();

  const locationCardStyleProps: Partial<LocationCardProps> = {
    editMode,
    verticalPadding,
    horizontalPadding,
    fullHeight: true,
    textStyle: detailedCardTextStyle,
    businessNameStyle: { fontWeight: 'bold', ...detailedCardHeadingStyle },
    alignment: 'left',
    businessHoursDisplay: detailCardElements.includes('businessHours')
      ? detailCardBusinessHoursDisplay
      : 'none',
    background: {
      type: 'color',
      color: 'rgba(0, 0, 0, 0.0001)'
    },
    dayMonthFormat,
    firstWeekday,
    timeFormat,
    openStatusColor,
    closedSoonStatusColor,
    closedStatusColor,
    textColor,
    iconsColor: getColorWithAlpha(textColor, 0.5),
    accentColor,
    language,
    ctaButtonsStyle,
    ctaButtonsSize,
    businessLogoSize: 76,
    businessAboutShortened: false,
    ...(elementsCornerRadius === 'circular' ? { ctaButtonsRadius: 100 } : {})
  };

  const locationCardDataProps: Partial<LocationCardProps> | undefined =
    useMemo(() => {
      const isDetailDisplayable = isDisplayable(detailCardElements);

      const conditionalDisplay = <T,>(
        element: DetailCardElement,
        value: T,
        unsetValue: T
      ) => (isDetailDisplayable(element) ? value : unsetValue);

      // activeLocation should stay until popup is completely closed
      const location = activeLocation || prevActiveLocationRef?.current;

      const locationLabels = location?.categories.map((id) => ({
        id,
        text: categories.find((category) => category.id === id)?.name
      }));

      return {
        ...location,
        locationLabels: conditionalDisplay('categories', locationLabels, []),
        contactsLocationAddress: location?.place?.address || '',
        contactsLocationName: location?.place?.name || '',
        businessName: conditionalDisplay('name', location?.name, undefined),
        contactsAddress: conditionalDisplay(
          'address',
          location?.address,
          undefined
        ),
        contactsPhone: conditionalDisplay('phone', location?.phone, undefined),
        contactsEmail: conditionalDisplay('email', location?.email, undefined),
        contactsWebsite: conditionalDisplay(
          'website',
          location?.website,
          undefined
        ),
        businessAbout: conditionalDisplay(
          'description',
          location?.description,
          undefined
        ),
        locationPhotoImage: conditionalDisplay('photo', location?.photo, null),
        businessLogo: conditionalDisplay('logo', location?.logo, null),
        ctaButtons: conditionalDisplay('buttons', location?.buttons, [])
      };
    }, [detailCardElements, activeLocation, prevActiveLocationRef, categories]);

  return (
    <LocationCardComponent
      {...locationCardStyleProps}
      {...locationCardDataProps}
      businessLogoStyle="overlap"
      footerComponent={footerComponent}
      businessAboutShortened
    />
  );
}
